import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Plus, Edit, Trash2, Save, X, Video, MessageSquare, Quote } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import PinAuth from '../components/PinAuth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminTestimonials = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [testimonials, setTestimonials] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    patientName: '',
    type: 'text',
    content: '',
    date: ''
  });

  useEffect(() => {
    const savedPin = sessionStorage.getItem('adminPin');
    if (savedPin) {
      // Verify PIN with backend
      axios.get(`${API}/admin/pin/verify?pin=${savedPin}`)
        .then(response => {
          if (response.data.valid) {
            setIsAuthenticated(true);
          }
        })
        .catch(() => {
          sessionStorage.removeItem('adminPin');
        });
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadTestimonials();
    }
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return <PinAuth onSuccess={() => setIsAuthenticated(true)} title="Admin Testimonials" icon={Quote} />;
  }

  const loadTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/testimonials`);
      setTestimonials(response.data);
    } catch (error) {
      console.error('Error loading testimonials:', error);
      toast.error('Failed to load testimonials');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({
      ...prev,
      type: value,
      content: '' // Reset content when switching type
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingId) {
        // Update existing testimonial
        await axios.patch(`${API}/testimonials/${editingId}`, formData);
        toast.success('Testimonial updated successfully');
      } else {
        // Create new testimonial
        await axios.post(`${API}/testimonials`, formData);
        toast.success('Testimonial created successfully');
      }
      
      resetForm();
      loadTestimonials();
    } catch (error) {
      console.error('Error saving testimonial:', error);
      toast.error('Failed to save testimonial');
    }
  };

  const handleEdit = (testimonial) => {
    setFormData({
      patientName: testimonial.patientName,
      type: testimonial.type,
      content: testimonial.content,
      date: testimonial.date
    });
    setEditingId(testimonial.id);
    setIsEditing(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;
    
    try {
      await axios.delete(`${API}/testimonials/${id}`);
      toast.success('Testimonial deleted successfully');
      loadTestimonials();
    } catch (error) {
      console.error('Error deleting testimonial:', error);
      toast.error('Failed to delete testimonial');
    }
  };

  const resetForm = () => {
    setFormData({
      patientName: '',
      type: 'text',
      content: '',
      date: ''
    });
    setIsEditing(false);
    setEditingId(null);
  };

  const videoTestimonials = testimonials.filter(t => t.type === 'video');
  const textTestimonials = testimonials.filter(t => t.type === 'text');

  return (
    <div className="admin-testimonials-page min-h-screen bg-gradient-to-br from-blue-50 to-white py-12">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Manage Testimonials</h1>
          <p className="text-gray-600">Add, edit, or remove patient testimonials</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Form */}
          <div className="lg:col-span-1">
            <Card className="border-none shadow-xl sticky top-4">
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  {isEditing ? 'Edit Testimonial' : 'Add New Testimonial'}
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="patientName">Patient/Family Name *</Label>
                    <Input
                      id="patientName"
                      name="patientName"
                      value={formData.patientName}
                      onChange={handleChange}
                      placeholder="John Doe"
                      required
                      className="border-blue-200 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <Label htmlFor="type">Testimonial Type *</Label>
                    <Select onValueChange={handleSelectChange} value={formData.type} required>
                      <SelectTrigger className="border-blue-200 focus:border-blue-500">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="text">Text Review</SelectItem>
                        <SelectItem value="video">Video Testimonial</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="content">
                      {formData.type === 'video' ? 'YouTube URL *' : 'Review/Quote *'}
                    </Label>
                    {formData.type === 'video' ? (
                      <Input
                        id="content"
                        name="content"
                        value={formData.content}
                        onChange={handleChange}
                        placeholder="https://www.youtube.com/watch?v=..."
                        required
                        className="border-blue-200 focus:border-blue-500"
                      />
                    ) : (
                      <Textarea
                        id="content"
                        name="content"
                        value={formData.content}
                        onChange={handleChange}
                        placeholder="Write the testimonial quote..."
                        rows={6}
                        required
                        className="border-blue-200 focus:border-blue-500 resize-none"
                      />
                    )}
                  </div>

                  <div>
                    <Label htmlFor="date">Date *</Label>
                    <Input
                      id="date"
                      name="date"
                      value={formData.date}
                      onChange={handleChange}
                      placeholder="January 2025"
                      required
                      className="border-blue-200 focus:border-blue-500"
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button 
                      type="submit" 
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      <Save size={16} className="mr-2" />
                      {isEditing ? 'Update' : 'Create'}
                    </Button>
                    
                    {isEditing && (
                      <Button 
                        type="button"
                        onClick={resetForm}
                        variant="outline"
                        className="border-gray-300"
                      >
                        <X size={16} />
                      </Button>
                    )}
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Testimonials List */}
          <div className="lg:col-span-2 space-y-8">
            {/* Video Testimonials */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Video className="text-blue-600" size={24} />
                <h2 className="text-2xl font-bold text-gray-900">
                  Video Testimonials ({videoTestimonials.length})
                </h2>
              </div>

              {videoTestimonials.length === 0 ? (
                <Card className="border-none shadow-lg">
                  <CardContent className="p-8 text-center">
                    <Video size={40} className="mx-auto text-gray-400 mb-2" />
                    <p className="text-gray-600">No video testimonials yet</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {videoTestimonials.map((testimonial) => (
                    <Card key={testimonial.id} className="border-none shadow-lg hover:shadow-xl transition-all">
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Video size={20} className="text-blue-600" />
                              <h3 className="text-xl font-bold text-gray-900">{testimonial.patientName}</h3>
                            </div>
                            <p className="text-gray-600 text-sm mb-3">{testimonial.date}</p>
                            <a 
                              href={testimonial.content} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-700 text-sm break-all"
                            >
                              {testimonial.content}
                            </a>
                            
                            <div className="flex gap-2 text-xs text-gray-500 mt-3">
                              <span className="bg-gray-100 px-2 py-1 rounded">
                                {new Date(testimonial.timestamp).toLocaleDateString()}
                              </span>
                              <span className="bg-green-100 text-green-800 px-2 py-1 rounded">
                                {testimonial.status}
                              </span>
                            </div>
                          </div>
                          
                          <div className="flex flex-col gap-2">
                            <Button
                              onClick={() => handleEdit(testimonial)}
                              variant="outline"
                              className="border-blue-600 text-blue-600 hover:bg-blue-50"
                              size="sm"
                            >
                              <Edit size={16} className="mr-1" />
                              Edit
                            </Button>
                            <Button
                              onClick={() => handleDelete(testimonial.id)}
                              variant="outline"
                              className="border-red-600 text-red-600 hover:bg-red-50"
                              size="sm"
                            >
                              <Trash2 size={16} className="mr-1" />
                              Delete
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Text Testimonials */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <MessageSquare className="text-blue-600" size={24} />
                <h2 className="text-2xl font-bold text-gray-900">
                  Text Reviews ({textTestimonials.length})
                </h2>
              </div>

              {textTestimonials.length === 0 ? (
                <Card className="border-none shadow-lg">
                  <CardContent className="p-8 text-center">
                    <MessageSquare size={40} className="mx-auto text-gray-400 mb-2" />
                    <p className="text-gray-600">No text testimonials yet</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {textTestimonials.map((testimonial) => (
                    <Card key={testimonial.id} className="border-none shadow-lg hover:shadow-xl transition-all">
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <h3 className="text-xl font-bold text-gray-900 mb-1">{testimonial.patientName}</h3>
                            <p className="text-gray-600 text-sm mb-3">{testimonial.date}</p>
                            <p className="text-gray-700 leading-relaxed italic">"{testimonial.content}"</p>
                            
                            <div className="flex gap-2 text-xs text-gray-500 mt-3">
                              <span className="bg-gray-100 px-2 py-1 rounded">
                                {new Date(testimonial.timestamp).toLocaleDateString()}
                              </span>
                              <span className="bg-green-100 text-green-800 px-2 py-1 rounded">
                                {testimonial.status}
                              </span>
                            </div>
                          </div>
                          
                          <div className="flex flex-col gap-2">
                            <Button
                              onClick={() => handleEdit(testimonial)}
                              variant="outline"
                              className="border-blue-600 text-blue-600 hover:bg-blue-50"
                              size="sm"
                            >
                              <Edit size={16} className="mr-1" />
                              Edit
                            </Button>
                            <Button
                              onClick={() => handleDelete(testimonial.id)}
                              variant="outline"
                              className="border-red-600 text-red-600 hover:bg-red-50"
                              size="sm"
                            >
                              <Trash2 size={16} className="mr-1" />
                              Delete
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminTestimonials;
